import React, { SetStateAction, useState } from "react";
import { Badge } from "react-bootstrap";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { IUser } from "../../../shared/interfaces/user/IUser";
import DeleteModal from "../../../shared/components/DeleteModal";
import { UserService } from "../service";
import { handleError } from "../../../shared/functions/handleError";
import { enqueueSnackbar } from "notistack";
import PageSizeSelect from "../../../shared/components/PageSizeSelect";
import Pagination from "../../../shared/components/Pagination";
import { IPagination } from "../../../shared/interfaces/shared/IPagination";

interface IProps {
  users: IUser[];
  pagination: IPagination;
  totalPages: number;
  handleUpdateList: () => void;
  changeFilterHandler: (
    name: string,
    value: string | number | boolean | number[] | null,
  ) => void;
}

const UserList = ({
  users,
  pagination,
  totalPages,
  handleUpdateList,
  changeFilterHandler,
}: IProps) => {
  const { t } = useTranslation();

  const [isDeleteVisible, setIsDeleteVisible] = useState(false);
  const [selectedUser, setSelectedUser] = useState<IUser | undefined>(
    undefined,
  );

  const handleCloseDelete = () => {
    setIsDeleteVisible(false);
    setSelectedUser(undefined);
  };

  const handleDeleteUser = (id: number) => {
    UserService.delete(id)
      .then((response) => {
        if (response) {
          handleUpdateList();
          handleCloseDelete();
          enqueueSnackbar(
            t("toasts.successfullyDeleted", {
              name: selectedUser?.firstName,
            }),
            {
              variant: "success",
            },
          );
        }
      })
      .catch(handleError);
  };
  return (
    <React.Fragment>
      <div className="table-responsive">
        <table className="table table-hover" id="pc-dt-simple">
          <thead>
            <tr>
              <th>{t("general.name")}</th>
              <th>{t("general.email")}</th>
              <th>{t("general.phone")}</th>
              <th>{t("general.roles")}</th>
              <th>{t("general.institution")}</th>
              <th>{t("general.enabled")}</th>
              <th>{t("general.action")}</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => {
              return (
                <tr key={user?.id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <div className="flex-shrink-0">
                        {user?.profileImageUrl ? (
                          <img
                            src={user.profileImageUrl}
                            alt={`${user.firstName} ${user.lastName}`}
                            className="img-radius wid-40"
                          />
                        ) : (
                          <div
                            className="avtar avtar-s bg-light-primary"
                            style={{ width: "40px", height: "40px" }}
                          >
                            {`${user?.firstName?.[0] ?? ""}${user?.lastName?.[0] ?? ""}`}
                          </div>
                        )}
                      </div>

                      <div className="flex-grow-1 ms-3">
                        <h6 className="mb-0">
                          {user?.firstName} {user?.lastName}
                        </h6>
                      </div>
                    </div>
                  </td>
                  <td>{user?.email}</td>
                  <td>{user?.phoneNumber}</td>
                  <td>{user?.roles.join(", ")}</td>
                  <td>{user?.institutionName}</td>
                  <td>
                    <Badge
                      bg={user?.enabled ? "success" : "danger"}
                      className="fw-semibold"
                    >
                      {user?.enabled
                        ? t("general.enabled")
                        : t("general.disabled")}
                    </Badge>
                  </td>

                  <td>
                    <Link
                      to={`/users/${user?.id}`}
                      className="avtar avtar-xs btn-link-secondary"
                    >
                      <i className="ti ti-edit f-20"></i>
                    </Link>
                    <Link
                      to="#"
                      onClick={() => {
                        setSelectedUser(user);
                        setIsDeleteVisible(true);
                      }}
                      className="avtar avtar-xs btn-link-secondary"
                    >
                      <i className="ti ti-trash f-20"></i>
                    </Link>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <PageSizeSelect
        value={pagination.size}
        onChange={(size) => changeFilterHandler("size", size)}
      />

      <Pagination
        currentPage={pagination.page}
        totalPages={totalPages}
        onPageChange={(page) => changeFilterHandler("page", page)}
      />
      <DeleteModal
        show={isDeleteVisible}
        handleClose={handleCloseDelete}
        handleDeleteId={() => handleDeleteUser(Number(selectedUser?.id))}
      />
    </React.Fragment>
  );
};

export default UserList;
